import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router';
import { NavLink } from 'react-router-dom';

import axios from 'axios';

import { Breadcrumb } from '@shared/components/bootstrap/breadcrumb';

import { ItemCategorizationModal } from '@admin/components/items/item_categorization_modal';
import { ItemGroupableGrid } from '@admin/components/items/item_groupable_grid';
import { ItemsTable } from '@admin/components/items/items_table';
import { ItemsCount } from '@admin/components/items_count';
import { Spinner } from '@admin/components/spinner';
import { useTitle } from '@admin/hooks/use_title';
import type { IItem } from '@admin/types/item';

interface AccountData {
  id: number;
  number: string;
  customer: { name: string };
}

interface OrderGroup {
  order: {
    id: number;
    account_id: number;
    number: string;
    scheduled: string;
    type: string;
    subtype: string;
    maintenance?: boolean;
  };
  items: IItem[];
}

interface NewItemGroupProps {
  accountID: string | null;
}

export const NewItemGroup: React.FC = () => {
  useTitle('New Item Group');
  const { accountID } = useParams<{ accountID: string }>();
  const [account, setAccount] = useState<AccountData | null>(null);
  const [groupedItems, setGroupedItems] = useState<OrderGroup[] | null>(null);
  const [selectedItems, setSelectedItems] = useState<IItem[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (!accountID) {
      setAccount(null);
      setGroupedItems(null);
      setSelectedItems([]);
      setShowModal(false);
      setLoadError(null);
      return;
    }

    setSelectedItems([]);
    setShowModal(false);
    setAccount(null);
    setGroupedItems(null);
    setLoadError(null);

    let cancelled = false;
    axios
      .get<AccountData>(`/accounts/${accountID}.json`)
      .then((res) => {
        if (!cancelled) setAccount(res.data);
      })
      .catch(() => {
        if (!cancelled) {
          setAccount(null);
          setLoadError('Unable to load account information. Please try again.');
        }
      });
    axios
      .get<OrderGroup[]>(`/accounts/${accountID}/items/groupables.json`)
      .then((res) => {
        if (!cancelled) setGroupedItems(res.data);
      })
      .catch(() => {
        if (!cancelled) {
          setGroupedItems([]);
          setLoadError('Unable to load groupable items. Please try again.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [accountID]);

  const handleConfirm = useCallback(() => {
    setShowModal(true);
  }, []);

  const handleModalSave = useCallback(() => {
    setShowModal(false);
    window.location.href = `/accounts/${accountID}/inventory`;
  }, [accountID]);

  const handleModalClose = useCallback(() => {
    setShowModal(false);
  }, []);

  if (!accountID) return null;

  const invalid = selectedItems.length < 2;

  return (
    <>
      <h1 className="page-header text-overflow">
        <Breadcrumb>
          <Breadcrumb.Item>
            <NavLink to="/accounts">Accounts</NavLink>
          </Breadcrumb.Item>{' '}
          {account && (
            <Breadcrumb.Item>
              <NavLink to={`/accounts/${account.id}`}>
                #{account.number} ({account.customer.name})
              </NavLink>
            </Breadcrumb.Item>
          )}{' '}
          {account && (
            <Breadcrumb.Item>
              <NavLink to={`/accounts/${account.id}/inventory`}>Inventory</NavLink>
            </Breadcrumb.Item>
          )}{' '}
          <Breadcrumb.Item active>New Item Group</Breadcrumb.Item>
        </Breadcrumb>
      </h1>

      {loadError && (
        <div className="alert alert-danger" role="alert">
          {loadError}
        </div>
      )}

      <Spinner visible={groupedItems === null} />

      {groupedItems && (
        <ItemGroupableGrid
          groupedItems={groupedItems}
          selections={selectedItems}
          onSelectionsChange={setSelectedItems}
        />
      )}

      {groupedItems && (
        <div className="panel">
          <div className="panel-heading">
            <div className="panel-title">
              <ItemsCount items={selectedItems} />
            </div>
          </div>
          <ItemsTable items={selectedItems} />
          <div className="panel-footer">
            <div className="clearfix">
              <div className="pull-right">
                <a href={`/accounts/${accountID}/inventory`} className="btn btn-default">
                  Cancel
                </a>
                <button className="btn btn-primary" disabled={invalid} onClick={handleConfirm}>
                  Create Item Group
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <ItemCategorizationModal
          items={selectedItems}
          accountID={accountID}
          mode="item_group"
          itemGroupID={null}
          onClose={handleModalClose}
          onSave={handleModalSave}
        />
      )}
    </>
  );
};
